import {PencilIcon} from '@heroicons/react/24/outline'
import React, {useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Mentor} from '../../domain/mentor'
import {
  MENTOR_ID_ROUTE_PLACEHOLDER,
  MENTOR_PROFILE_ROUTE
} from '../../global-constants/routingConstants'
import useMentors, {MentorQuery} from '../../hooks/useMentors'
import Divider from '../Divider'
import FlatButton from '../FlatButton'
import {InternalLinkStyledAsFlatButton} from '../links/InternalLink'
import LoadingIndicator from '../LoadingIndicator'
import TextInput from '../TextInput/AdminTextInput'
import RadioButtonGroupInput from './RadioButtonGroupInput'
import {
  Footer,
  FormContainer,
  Table,
  TransitionWrapper,
  UserTypeSelector
} from './sections'
import {UserType} from './types'

export type MentorSearchIndex = 'email' | 'name'

function SearchMentor({
  defaultPageSize,
  setSelectedUserType,
  setError
}: {
  setError: (error: boolean) => void
  defaultPageSize: number
  setSelectedUserType: (userType: UserType) => void
}) {
  const intl = useIntl()
  const {error, isLoading, mentors, refetchMentors} = useMentors({
    enabled: false,
    initialQuery: {pageSize: defaultPageSize}
  })
  const [mentorSearchIndex, setMentorSearchIndex] =
    useState<MentorSearchIndex>('email')
  const [emailSearchText, setEmailSearchText] = useState('')
  const [firstNameSearchText, setFirstNameSearchText] = useState('')
  const [lastNameSearchText, setLastNameSearchText] = useState('')

  const [searchTextErrorMessage, setSearchTextErrorMessage] = useState<
    string | null
  >(null)

  function submitSearch() {
    const query: MentorQuery = {pageSize: defaultPageSize}
    if (mentorSearchIndex === 'email') {
      const trimmedEmailSearchText = emailSearchText.trim()
      query.email = trimmedEmailSearchText ? trimmedEmailSearchText : undefined
    } else {
      const trimmedFirstNameSearchText = firstNameSearchText.trim()
      const trimmedLastNameSearchText = lastNameSearchText.trim()
      query.firstName = trimmedFirstNameSearchText
        ? trimmedFirstNameSearchText
        : undefined
      query.lastName = trimmedLastNameSearchText
        ? trimmedLastNameSearchText
        : undefined
    }
    refetchMentors(query)
  }

  function clearSearch() {
    setEmailSearchText('')
    setFirstNameSearchText('')
    setLastNameSearchText('')
    refetchMentors()
  }

  useEffect(() => {
    if (error) {
      setError(true)
      window.scrollTo({top: 0})
    } else {
      setError(false)
    }
  }, [error, setError])

  return (
    <div>
      <FormContainer>
        <UserTypeSelector onSelect={setSelectedUserType} selected="mentor" />
        <TransitionWrapper>
          <Divider />
          <RadioButtonGroupInput
            name="user-search-type-input"
            value={mentorSearchIndex}
            label={'admin.userSettings.searchIndexLabel'}
            onChange={setMentorSearchIndex}
            options={[
              {
                value: 'email',
                label: intl.formatMessage({
                  id: 'admin.userSettings.searchType.email'
                })
              },
              {
                value: 'name',
                label: intl.formatMessage({
                  id: 'admin.userSettings.searchType.name'
                })
              }
            ]}
          />
          <Divider />
          <div className="pt-2">
            {mentorSearchIndex === 'email' && (
              <TextInput
                autoCapitalize="words"
                errorMessage={searchTextErrorMessage}
                id="email-search"
                inputMode="text"
                label={intl.formatMessage({
                  id: 'admin.userSettings.emailLabel'
                })}
                onChange={event => {
                  setEmailSearchText(event.target.value)
                  setSearchTextErrorMessage(null)
                }}
                required
                type="search"
                value={emailSearchText}
              />
            )}
            {mentorSearchIndex === 'name' && (
              <>
                <TextInput
                  autoCapitalize="words"
                  errorMessage={searchTextErrorMessage}
                  id="first-name-search"
                  inputMode="text"
                  label={intl.formatMessage({
                    id: 'admin.userSettings.firstNameLabel'
                  })}
                  onChange={event => {
                    setFirstNameSearchText(event.target.value)
                    setSearchTextErrorMessage(null)
                  }}
                  required
                  type="text"
                  value={firstNameSearchText}
                />
                <TextInput
                  autoCapitalize="words"
                  errorMessage={searchTextErrorMessage}
                  id="last-name-search"
                  inputMode="text"
                  label={intl.formatMessage({
                    id: 'admin.userSettings.lastNameLabel'
                  })}
                  onChange={event => {
                    setLastNameSearchText(event.target.value)
                    setSearchTextErrorMessage(null)
                  }}
                  required
                  type="text"
                  value={lastNameSearchText}
                />
              </>
            )}
          </div>
        </TransitionWrapper>
      </FormContainer>
      <TransitionWrapper className="my-8 flex justify-center space-x-4">
        <FlatButton
          onClick={submitSearch}
          onKeyPress={event => {
            if (event.key === 'Enter') {
              submitSearch()
            }
          }}
          variant="primary"
          type="button"
        >
          <FormattedMessage id="admin.userSettings.searchSubmit" />
        </FlatButton>
        <FlatButton
          onClick={clearSearch}
          onKeyPress={event => {
            if (event.key === 'Enter') {
              clearSearch()
            }
          }}
          variant="primary"
          type="button"
        >
          <FormattedMessage id="general.clear" />
        </FlatButton>
      </TransitionWrapper>
      {!mentors && isLoading && <LoadingIndicator />}
      {mentors && mentors.length > 0 && (
        <Table<Mentor>
          isLoading={isLoading}
          columns={[
            {
              label: '',
              value: ({id}) => (
                <div className="flex justify-end">
                  <InternalLinkStyledAsFlatButton
                    variant="secondary"
                    icon={<PencilIcon />}
                    to={MENTOR_PROFILE_ROUTE.replace(
                      MENTOR_ID_ROUTE_PLACEHOLDER,
                      id
                    )}
                  >
                    Edit
                  </InternalLinkStyledAsFlatButton>
                </div>
              )
            },
            {
              label: 'admin.userSettings.nameColumn',
              value: ({firstName, lastName}) => `${firstName} ${lastName}`
            },
            {
              label: 'admin.userSettings.communityColumn',
              value: ({communityName}) => communityName
            },
            {
              label: 'admin.userSettings.searchType.email',
              value: ({email}) => email
            }
          ]}
          rows={mentors}
        />
      )}
      {mentors && (
        <Footer rowCount={mentors.length} defaultPageSize={defaultPageSize} />
      )}
    </div>
  )
}

export default SearchMentor
